import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [checkingApproval, setCheckingApproval] = useState(true);
  const sub = useSubscription();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    } else if (user) {
      checkApprovalStatus();
    }
  }, [user, loading, navigate]);

  const checkApprovalStatus = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("approval_status")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      setApprovalStatus(data.approval_status);
      if (data.approval_status !== "approved") navigate("/pending-approval");
    } catch (error) {
      console.error("Error checking approval status:", error);
    } finally {
      setCheckingApproval(false);
    }
  };

  // Billing gate (after approval is confirmed)
  useEffect(() => {
    if (approvalStatus !== "approved") return;
    if (sub.status !== "ready") return;
    if (sub.hasAccess) return;

    // /profile is always allowed so users can pay / manage billing
    if (location.pathname === "/profile") return;

    if (!sub.subscription) {
      navigate("/welcome", { replace: true });
    } else {
      navigate("/profile#billing", { replace: true });
    }
  }, [approvalStatus, sub, navigate, location.pathname]);

  if (loading || checkingApproval || sub.status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (!user || approvalStatus !== "approved") return null;

  // Block render unless allowed (profile bypass) so we don't flash gated content
  const onProfile = location.pathname === "/profile";
  if (!sub.hasAccess && !onProfile) return null;

  return <>{children}</>;
};
